import React, {useCallback, useEffect, useState} from 'react';
import {Button, Checkbox, Col, Drawer, notification, PageHeader, Row, Select} from 'antd';
import {CloseCircleFilled, ReloadOutlined,} from '@ant-design/icons';
import moment from 'moment';
import {useUserPermissions} from '../../auth/userHooks';
import {getD365Exports} from '../../api/d365Api';
import _ from 'lodash';
import {ExportButton} from './ExportButton';
import {HourLog} from './HourLog';
import {PREFERRED_DATE_FORMAT} from "../../util/time";
import {ExportsTable} from "./ExportsTable";
import {PSA_INSTANCE} from "../Constants";


const Exports = () => {
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(false);
  const permissions = useUserPermissions();
  const [editMap, setEditMap] = useState({});
  const [selectedExport, setSelectedExport] = useState(null);
  const [showHourLog, setShowHourLog] = useState(null);
  const [disableExports, setDisableExports] = useState(false);
  const [pendingProjectCodes, setPendingProjectCodes] = useState([])
  const [hideColumns, setHideColumns] = useState(true);
  const [projector, setProjector] = useState(null);
  const [filteredExports, setFilteredExports] = useState(exports);


  const filterExports = useCallback((exports, projector) => {
    if (!projector) {
      setFilteredExports(exports);
    } else {
      setFilteredExports(exports.filter(exp => exp.PsaInstance === projector));
    }
  }, []);

  const loadExports = async () => {
    try {
      setLoading(true);
      const exports = await getD365Exports();
      const editMap = {};
      let hasPending = 0;
      let projectCodes = [];
      _.each(exports, e => {
        if (["PENDING", "COMPLETED"].includes(e.exportStatus) &&
          ["NONE", "GENERATED", "SUBMITTED"].includes(e.loadStatus)) {
          // if no projectCodes were provided it means all projectCodes will be processed
          if (_.isEmpty(e.projectCodes)) {
            hasPending++;
          } else {
            projectCodes = projectCodes.concat(e.projectCodes.split(","));
          }
        }
        editMap[e.ID] = false;
      });
      setExports(exports);
      filterExports(exports, projector);
      setEditMap(editMap);
      setPendingProjectCodes(projectCodes)
      setDisableExports(hasPending > 0);
    } catch (e) {
      console.error(`Could not get D365 exports`);
    } finally {
      setLoading(false);
    }
  };

  const handleExportResponse = (type, response) => {
    if (response && response.error) {
      notificationError(response.error)
    } else if (response && response.status && response.status === "ok") {
      notification.success({
        message: 'Success',
        description: `${_.capitalize(type)} export has been started!`,
        placement: 'bottomRight',
      });
    } else {
      notification.warning({
        message: 'Export Failed',
        description: `${_.capitalize(type)} export failed to start. Please contact na_opsenablement@credera.com`,
        placement: 'bottomRight',
      });
    }
    loadExports();
  };

  const notificationError = (msg) => {
    console.error(msg)
    notification.error({
      message: 'Error',
      description: msg,
      placement: 'bottomRight',
      duration: 10,
    });
  }

  const hideHourLog = () => {
    setSelectedExport(null);
    setShowHourLog(false);
  };

  const handleCheckbox = (t) => {
    setHideColumns(!t.target.checked)
  }

  useEffect(() => {
    loadExports();
    // eslint-disable-next-line
  }, []);

  const selectProjector = (value) => {
    setProjector(value);
    filterExports(exports, value);
  }

  return (
    <>
      <Row>
        <Col span={12}>
          <PageHeader
            title="D365 Exports"
            subTitle={`(${exports.length} shown)`}
            style={{padding: 0}}
          />
        </Col>
        <Col span={12}>
          <Button
            style={{float: 'right', alignItems: 'center'}}
            onClick={() => loadExports()}
            loading={loading}
            icon={<ReloadOutlined/>}
          >
            Refresh
          </Button>
          {permissions !== null && permissions.canManageD365 && (
            <>
              <ExportButton exportType="HOURS"
                            includeDateFilter includeProjectCodes includeWorkerIds
                            disabled={loading || disableExports}
                            pendingProjectCodes={pendingProjectCodes}
                            onExport={handleExportResponse} style={{float: 'right', marginRight: 10}}/>
              <ExportButton exportType="RATES"
                            includeProjectCodes
                            disabled={loading || disableExports}
                            pendingProjectCodes={pendingProjectCodes}
                            onExport={handleExportResponse} style={{float: 'right', marginRight: 10}}/>
            </>
          )}
          <Checkbox
            onChange={handleCheckbox}
            disabled={loading}
            style={{float: 'right', marginRight: 10}}
          >
            Display Export Data
          </Checkbox>
        </Col>
      </Row>

      <Row style={{marginBottom: 12, alignItems: 'center'}}>
        <span style={{marginRight: 8}}>Projector:</span>
        <Select
          value={projector}
          onChange={selectProjector}
          options={PSA_INSTANCE}
          allowClear
          placeholder="Select Projector"
          suffixIcon={
            <CloseCircleFilled
              style={{color: 'rgba(0, 0, 0, 0.25)', cursor: 'pointer', pointerEvents: 'auto'}}
              onClick={e => {
                e.stopPropagation();
                setProjector(null);
              }}
            />
          }
          style={{width: 160}}
        />
      </Row>

      <ExportsTable
        exports={filteredExports}
        permissions={permissions}
        loading={loading}
        setLoading={setLoading}
        hideColumns={hideColumns}
        editMap={editMap}
        setEditMap={setEditMap}
        pendingProjectCodes={pendingProjectCodes}
        notificationError={notificationError}
        loadExports={loadExports}
        disableExports={disableExports}
        setSelectedExport={setSelectedExport}
        setShowHourLog={setShowHourLog}
      />

      <Drawer
        title={"Hour Log Records for Export " + selectedExport?.filename +
          " created by " + selectedExport?.createdByEmail +
          ", last updated on " + (selectedExport ? moment.utc(selectedExport.UpdatedAt).format(PREFERRED_DATE_FORMAT) : "")}
        placement="bottom"
        closable={true}
        onClose={hideHourLog}
        open={showHourLog}
        height="100%"
      >
        {selectedExport && (
          <HourLog exp={selectedExport}/>
        )}
      </Drawer>
    </>
  );
};

export {Exports};
